import type { ReturnSchedulingRequestView } from '@/dashboard/api';
/**
 * Checkout-handoff view helpers
 * (TREATMENT-ESTIMATE-ACCEPTANCE-AND-PATIENT-ACCOUNT-SPINE-V1).
 *
 * Pure, react-free logic for the receptionist checkout summary rendered on
 * /dashboard/billing-entry when an episode is prefilled: wire mirrors of
 * api/routes/checkout-handoff.ts + api/routes/treatment-discounts.ts,
 * FIELDS-NOT-SENTENCES label maps, and the door builders that map the
 * EXACTLY-ONE primary action token onto EXISTING lawful flows (render-only;
 * the backend gates are the boundary; a door never re-picks its episode --
 * LES-022).
 *
 * PAYMENT HONESTY: the wire's accountBalance is ALWAYS the literal
 * 'not_available' (gap.patient-account-model); the copy below never says
 * "Balance due" / "Outstanding balance" / "Fully paid" at the account
 * grain. The per-episode outstanding is the billing queue's own lawful
 * vocabulary, rendered under its own label. Unknown prices render "Price
 * unavailable", never a fabricated number.
 */

import { formatMinorUnitsInr, type BillingQueueEpisodeState, type FinalizedBillSummary } from '@/dashboard/billing/billing-entry-view';

/** Select the server's bill liability when present. Cash received and the
 * treatment estimate remain separate facts, including for legacy payments
 * whose allocation is unknown. No balance is calculated in the browser. */
export function collectionOutstandingMinorUnits(
  handoff: Pick<CheckoutHandoffResponse, 'finalizedBills' | 'outstandingMinorUnits'>,
): number | null {
  const bills = handoff.finalizedBills;
  if (bills && bills.billCount > 0) {
    return bills.state === 'known' ? bills.dueMinorUnits : null;
  }
  return handoff.outstandingMinorUnits;
}

/** Display the existing estimate's settled-null convention only with complete
 * priced evidence. Bill state and partial/unknown prices are not proof of zero. */
export function treatmentEstimateOutstanding(handoff: CheckoutHandoffResponse): number | null {
  if (handoff.outstandingMinorUnits !== null) return handoff.outstandingMinorUnits;
  const accepted = handoff.acceptedValuedSumInr;
  return !['cancelled', 'no_show'].includes(handoff.storedStatus) &&
    handoff.acceptedCount > 0 && handoff.notConfiguredCount === 0 &&
    accepted !== null && accepted >= 0 && Number.isSafeInteger(accepted * 100) &&
    Number.isSafeInteger(handoff.collectedMinorUnits) && handoff.collectedMinorUnits >= accepted * 100
    ? 0 : null;
}

/** Render saved bill allocations separately from receipts linked to a visit. */
export function visitBillPaymentSummary(handoff: Pick<CheckoutHandoffResponse, 'finalizedBills'>): string {
  const bills = handoff.finalizedBills;
  if (!bills) return 'Bill payment unavailable';
  if (bills.billCount === 0 && bills.state === 'none') return 'No finalized bills on this visit';
  if (bills.state !== 'known' || bills.paidMinorUnits === null || bills.dueMinorUnits === null)
    return bills.state === 'allocation_unknown' ? 'Bill allocation unknown' : 'Bill payment unavailable';
  const status = bills.dueMinorUnits === 0 ? 'Bills settled' : bills.paidMinorUnits > 0 ? 'Bills partly paid' : 'Bills unpaid';
  return `${formatMinorUnitsInr(bills.paidMinorUnits)} · ${status}`;
}

// ---- Wire mirrors (api/routes/checkout-handoff.ts) -------------------------

export type CheckoutPrimaryActionToken =
  | 'record_payment'
  | 'schedule_next_appointment'
  | 'review_unresolved_items'
  | 'complete_checkout'
  | 'no_action_required';

/** Wire mirror of the CONVERSION STATE LAW vocabulary (corrective
 *  2026-07-14): converted states derive ONLY from active non-voided
 *  first-payment evidence -- never acceptance / estimate / billing alone.
 *  'converted_chief_complaint' is RESERVED on the backend (no lawful
 *  item-level chief-complaint owner yet) but mirrored so the label map is
 *  total. */
export type CommercialConversionStateToken =
  | 'no_treatment_recommended'
  | 'undecided'
  | 'accepted_payment_pending'
  | 'partially_accepted_payment_pending'
  | 'declined'
  | 'deferred'
  | 'converted_first_payment'
  | 'converted_chief_complaint'
  | 'converted_full_package';

/** Doctor-proposed payment STRUCTURE (a plan, never payment; never a
 *  conversion signal). 'not_recorded' is the honest no-owner-yet state. */
export type ProposedPaymentStructureToken =
  | 'full'
  | 'partial'
  | 'advance'
  | 'emi'
  | 'treatment_wise'
  | 'package_wise'
  | 'not_recorded';

export interface CheckoutDecisionCountsMirror {
  readonly itemCount: number;
  readonly acceptedCount: number;
  readonly declinedCount: number;
  readonly deferredCount: number;
  readonly undecidedCount: number;
}

export interface CheckoutEstimateSummaryMirror {
  readonly recommendedValueInr: number | null;
  readonly acceptedValueInr: number | null;
  readonly deferredValueInr: number | null;
  readonly unknownPriceItemCount: number;
  readonly unknownPriceAcceptedCount: number;
  readonly estimateChangedSinceDiscount: boolean;
}

export interface CheckoutDiscountMirror {
  readonly state: 'none' | 'applied' | 'unavailable';
  readonly id: number | null;
  readonly originalAcceptedValueInr: number | null;
  readonly discountAmountInr: number | null;
  readonly finalAcceptedValueInr: number | null;
  readonly discountKind: 'percent' | 'fixed' | null;
  readonly discountPercent: number | null;
  readonly reasonCode: string | null;
  readonly approvedByRole: string | null;
  readonly approvedAtIso: string | null;
}

/** Minimal role-safe plan mirror: only the fields EVERY audience shape may
 *  carry are read (per-item rows render only when the caller's audience
 *  ships them -- billing/operational; analytics ships counts only). */
export interface CheckoutPlanItemMirror {
  readonly id: number;
  readonly procedureName: string;
  readonly estimatedValue: number | null;
  readonly status: string;
}

export interface CheckoutPlanMirror {
  readonly audience: 'clinical' | 'operational' | 'billing' | 'analytics';
  /** The case-sheet id (every audience shape carries it) -- the discount
   *  routes' anchor. */
  readonly id: number;
  readonly planItems?: readonly CheckoutPlanItemMirror[];
}

export interface CheckoutHandoffResponse {
  readonly finalizedBills?: FinalizedBillSummary;
  readonly appointmentId: number;
  readonly clinicId: number;
  readonly appointmentTimeIso: string;
  readonly patientLabel: string | null;
  readonly patientId: string | null;
  readonly leadId: number | null;
  readonly storedStatus: string;
  readonly visitStatus: 'none' | 'draft' | 'finalized';
  readonly queueState: BillingQueueEpisodeState;
  readonly acceptedCount: number;
  readonly acceptedValuedSumInr: number | null;
  readonly notConfiguredCount: number;
  readonly collectedMinorUnits: number;
  readonly outstandingMinorUnits: number | null;
  readonly doorAppointmentId: number;
  readonly doorSuggestedAmountInr: number | null;
  readonly decisionCounts: CheckoutDecisionCountsMirror;
  readonly estimate: CheckoutEstimateSummaryMirror;
  readonly discount: CheckoutDiscountMirror;
  readonly commercialConversionState: CommercialConversionStateToken;
  readonly proposedPaymentStructure: ProposedPaymentStructureToken;
  readonly paymentEvidence: 'none_recorded' | 'partial_recorded' | 'recorded';
  readonly unlinkedPaymentCount: number | null;
  readonly accountBalance: 'not_available';
  /** RECEPTION-MONEY-TRUTH-V1 (A2): the patient-grain money block beside
   *  the episode's own numbers -- THE ONE patient-grain owner's wire
   *  (lib/patient-money.ts). OPTIONAL so every pre-A2 fixture stays valid;
   *  null/absent renders NO prior-money facts (honest omission). */
  readonly patientMoney?: {
    readonly paidToDateMinorUnits: number;
    readonly scopeClinicCount?: number;
    readonly historyCapped?: boolean;
    readonly carriedMinorUnits: number;
    readonly carriedNotConfiguredCount: number;
    readonly collectedTodayMinorUnits: number;
    readonly lastPayment: {
      readonly occurredAtIso: string;
      readonly amountMinorUnits: number;
      readonly method: string | null;
    } | null;
  } | null;
  readonly nextAppointment: 'scheduled' | 'none' | 'unavailable';
  readonly followUp: 'required' | 'none' | 'unavailable';
  /** The doctor's FINALIZED follow-up recommendation
   *  (DOCTOR-CLINICAL-ENCOUNTER-FOUNDATION-V1) -- operational handoff
   *  content only (option token + configured label + booking instruction;
   *  never the clinical body). null = none to act on. OPTIONAL so
   *  pre-package fixtures stay valid. optionLabel null = the stored token
   *  left the org vocabulary (render the raw token, never invent). */
  readonly followUpRecommendation?: {
    readonly returnRequest?: ReturnSchedulingRequestView;
    readonly optionKey: string;
    readonly optionLabel: string | null;
    readonly note: string | null;
  } | null;
  readonly primaryAction: {
    readonly action: CheckoutPrimaryActionToken;
    readonly reason: string;
  };
  readonly canApplyDiscount: boolean;
  readonly discountReasonCodes: readonly string[];
  /** SMART-PAYMENT-DRAWER-V1 -- the in-place collect drawer's affordance +
   *  header facts. OPTIONAL so every pre-package fixture stays valid.
   *  canRecordPayment absent/false REMOVES the drawer's primary action
   *  (absent, never rendered-but-disabled). patientUmr null renders NO UMR
   *  field (an honest omission, never an empty label). */
  readonly canRecordPayment?: boolean;
  readonly clinicLabel?: string | null;
  readonly patientUmr?: string | null;
  readonly plan: CheckoutPlanMirror | null;
  /** Multi-visit course context (MULTI-VISIT-TREATMENT-JOURNEY-V1). null =
   *  no course linked (single-visit checkout, unchanged). OPTIONAL so
   *  pre-package fixtures stay valid. */
  readonly courseContext?: CheckoutCourseContextMirror | null;
}

/** Mirror of the backend CheckoutCourseContext (the course-grain remainder
 *  beside the episode door -- evidence, never a re-picked door; LES-022). */
export interface CheckoutCourseContextMirror {
  readonly state: 'linked' | 'unavailable';
  readonly courseId: number | null;
  readonly courseName: string | null;
  readonly visitCount: number | null;
  readonly progress: {
    readonly itemCount: number;
    readonly acceptedCount: number;
    readonly completedCount: number;
    readonly remainingPlannedCount: number;
  } | null;
  readonly completionAvailability: 'available' | 'unavailable' | null;
  readonly ledger: {
    readonly closedAcceptedValuedSumInr: number | null;
    readonly closedNotConfiguredCount: number;
    readonly collectedMinorUnits: number;
    readonly state:
      | 'outstanding'
      | 'not_configured'
      | 'settled'
      | 'no_accepted_plan'
      | 'not_closed';
    readonly outstandingMinorUnits: number | null;
  } | null;
}

/** Wire mirror of api/routes/treatment-discounts.ts TreatmentDiscountWire. */
export interface TreatmentDiscountWireMirror {
  readonly id: number;
  readonly caseSheetId: number;
  readonly clinicId: number;
  readonly originalAcceptedValueInr: number;
  readonly discountKind: 'percent' | 'fixed';
  readonly discountPercent: number | null;
  readonly discountAmountInr: number;
  readonly finalAcceptedValueInr: number;
  readonly reasonCode: string;
  readonly approvedByRole: string;
  readonly approvedAtIso: string;
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
}

// ---- Labels (FIELDS-NOT-SENTENCES; controlled tokens -> display copy) -------

export const CHECKOUT_PRIMARY_ACTION_LABELS: Readonly<
  Record<CheckoutPrimaryActionToken, string>
> = {
  record_payment: 'Record payment',
  schedule_next_appointment: 'Schedule next appointment',
  review_unresolved_items: 'Review unresolved treatment items',
  complete_checkout: 'Complete checkout',
  no_action_required: 'No action required',
};

export const CHECKOUT_ACTION_REASON_COPY: Readonly<Record<string, string>> = {
  collect_bill_due: 'The finalized bill has an unpaid balance.',
  bill_allocation_unavailable: 'Review the bill allocations before treating this bill as settled.',
  visit_terminal: 'Cancelled / no-show visit - nothing to collect.',
  visit_not_closed_undecided_items:
    'The visit is still open and treatment items await a patient decision.',
  visit_not_closed: 'The visit is not closed yet.',
  collect_outstanding: 'Accepted treatment has an uncollected amount on this visit.',
  collect_amount_not_configured:
    'Accepted treatment exists but no amount is configured - record what was collected.',
  close_pending_after_finalize:
    'The clinical record is locked; the front desk completes the visit on the day board.',
  accepted_needs_next_appointment:
    'Treatment was accepted and no future appointment exists yet.',
  checkout_settled: 'Collections recorded for this visit; nothing further is due here.',
  no_open_items: 'No open checkout items on this visit.',
};

export const NEXT_APPOINTMENT_LABELS: Readonly<Record<string, string>> = {
  scheduled: 'Next appointment scheduled',
  none: 'No next appointment yet',
  unavailable: 'Next-appointment state unavailable',
};

export const FOLLOW_UP_LABELS: Readonly<Record<string, string>> = {
  required: 'Follow-up required',
  none: 'No open follow-up',
  unavailable: 'Follow-up state unavailable',
};

export const PAYMENT_EVIDENCE_LABELS: Readonly<Record<string, string>> = {
  none_recorded: 'No payment recorded on this visit',
  partial_recorded: 'Payment recorded on this visit',
  recorded: 'Payment recorded on this visit',
};

/** Course-ledger state labels (MULTI-VISIT-TREATMENT-JOURNEY-V1). The SAME
 *  honesty vocabulary as the episode netting: an unknown accepted amount is
 *  its own state, never Rs 0; open-visit items are disclosed, not summed. */
export const COURSE_LEDGER_STATE_LABELS: Readonly<Record<string, string>> = {
  outstanding: 'Plan remainder due',
  not_configured: 'Accepted amount not configured',
  settled: 'Plan settled',
  no_accepted_plan: 'No accepted items on this journey yet',
  not_closed: 'Accepted items await their visit close',
};

/**
 * GRAIN-HONEST FACT LABELS (CHECKOUT-GRAIN-HONESTY-V1 -- issue #311 /
 * FND-37).
 *
 * THE DEFECT, established first-hand: the checkout panel renders an
 * EPISODE-grain conversion state (this appointment's item decisions +
 * this episode's netted collection) beside a COURSE-grain roll-up
 * (deriveCourseLedger over the course's visits). On visit 2 of a course
 * paid at visit 1 that reads as "Accepted - payment pending" beside "Plan
 * settled" -- two true sentences that tell one contradictory story,
 * because NEITHER STATES ITS SCOPE.
 *
 * The correction is LABELLING ONLY. No number moves, no derivation
 * changes, and the course roll-up is not hidden -- it is deliberate
 * evidence. The scope qualifier appears ONLY when a course actually
 * renders beside the episode: with no second grain on screen there is
 * nothing to tell apart, so the SINGLE-VISIT checkout (courseContext
 * null) stays BYTE-IDENTICAL to today.
 */
export type CheckoutFactKey =
  | 'conversion'
  | 'accepted'
  | 'decisions'
  | 'recommended'
  | 'discount'
  | 'payment'
  | 'course';

/** Single-visit checkout: one grain on screen, so no qualifier is earned. */
const CHECKOUT_FACT_LABELS_SINGLE_VISIT: Readonly<
  Record<CheckoutFactKey, string>
> = {
  conversion: 'Treatment status',
  accepted: 'Accepted items',
  decisions: 'Deferred / declined / undecided',
  recommended: 'Recommended value',
  discount: 'Discount',
  payment: 'Payment',
  course: 'Treatment journey',
};

/** A course renders beside the episode: every figure names its own scope,
 *  in the receptionist's words ("this visit" / "the whole course"), so the
 *  left column of the fact list reads as the grain it belongs to. */
const CHECKOUT_FACT_LABELS_WITH_COURSE: Readonly<
  Record<CheckoutFactKey, string>
> = {
  conversion: 'This visit - treatment status',
  accepted: 'This visit - accepted items',
  decisions: 'This visit - deferred / declined / undecided',
  recommended: 'This visit - recommended value',
  discount: 'This visit - discount',
  payment: 'This visit - payment',
  course: 'Whole course - treatment journey',
};

/**
 * The fact labels for THIS checkout. `coursePresent` is true whenever a
 * course context rides the wire at all -- 'linked' OR 'unavailable': a
 * faulted lane still puts a second grain on the screen (as an honest
 * "unavailable" row), so the episode figures still owe their scope.
 */
export function checkoutFactLabels(
  coursePresent: boolean,
): Readonly<Record<CheckoutFactKey, string>> {
  return coursePresent
    ? CHECKOUT_FACT_LABELS_WITH_COURSE
    : CHECKOUT_FACT_LABELS_SINGLE_VISIT;
}

/** The course roll-up's own money words. Each names the course, so no
 *  course figure can be read as this visit's balance. The AMOUNTS are
 *  untouched -- these are the words beside them, nothing more. */
export const COURSE_TOTAL_COPY = 'course total';
export const COURSE_COLLECTED_COPY = 'collected across the course';
export const COURSE_REMAINING_COPY = 'remaining across the course';

export const COURSE_CONTEXT_UNAVAILABLE_COPY =
  'Treatment journey unavailable';
export const COURSE_COMPLETION_UNAVAILABLE_COPY =
  'Completion evidence unavailable';

/** THE CONVERSION VOCABULARY (corrective section F -- the ALLOWED list,
 *  verbatim). Converted labels appear ONLY behind first-payment evidence;
 *  the forbidden list ("Converted based on acceptance alone", "Fully paid",
 *  "Zero balance", "Balance due", "Outstanding balance", "Account settled",
 *  "Final invoice", "Treatment completed") is test-pinned ABSENT. */
export const COMMERCIAL_CONVERSION_LABELS: Readonly<
  Record<CommercialConversionStateToken, string>
> = {
  no_treatment_recommended: 'No treatment recommended',
  undecided: 'Undecided',
  accepted_payment_pending: 'Accepted - payment pending',
  partially_accepted_payment_pending: 'Partially accepted - payment pending',
  declined: 'Declined',
  deferred: 'Deferred',
  converted_first_payment: 'First payment received · Converted',
  converted_chief_complaint: 'Converted - Chief Complaint',
  // Full package describes accepted treatment scope, never payment in full.
  converted_full_package: 'Full plan accepted · First payment received · Converted',
};

/** Doctor-proposed payment-structure labels (a plan, never payment). */
export const PROPOSED_PAYMENT_STRUCTURE_LABELS: Readonly<
  Record<ProposedPaymentStructureToken, string>
> = {
  full: 'Full payment (proposed)',
  partial: 'Partial payment (proposed)',
  advance: 'Advance (proposed)',
  emi: 'EMI (proposed)',
  treatment_wise: 'Treatment-wise (proposed)',
  package_wise: 'Package-wise (proposed)',
  not_recorded: 'Payment structure not recorded',
};

/** The standing account-honesty line (the Patient 360 vocabulary, reused). */
export const CHECKOUT_BALANCE_COPY = 'Account balance not available';

/** The doctor/reception COMMERCIAL HANDOFF boundary (corrective section E,
 *  Deepak rules 7-8), stated in-product on the checkout panel: the doctor
 *  owns the recommendation, item decisions, estimate, negotiated amount,
 *  proposed payment structure, and treatment outcome; the front desk owns
 *  collection, payment recording, checkout completion, receipt state, and
 *  scheduling. The panel is structurally read-only over the doctor plane
 *  (no decision/estimate editor exists here) -- reception never silently
 *  edits doctor-finalized state. */
export const CHECKOUT_BOUNDARY_COPY =
  'Treatment decisions and the estimate are doctor-owned; the front desk records collections and scheduling.';

/** Unknown-price honesty (never a fabricated number, never Rs 0). */
export const PRICE_UNAVAILABLE_COPY = 'Price unavailable';

/** Discount reason token -> friendly label (falls back to the raw token --
 *  org-configured tokens stay renderable). */
export const DISCOUNT_REASON_LABELS: Readonly<Record<string, string>> = {
  promotional_offer: 'Promotional offer',
  loyalty_patient: 'Loyalty patient',
  financial_hardship: 'Financial hardship',
  staff_or_family: 'Staff or family',
  doctor_negotiated: 'Doctor negotiated',
  management_approval: 'Management approval',
  other: 'Other',
};

export function discountReasonLabel(token: string): string {
  return DISCOUNT_REASON_LABELS[token] ?? token;
}

/** Approver-role display labels for the DISCOUNT surfaces (corrective
 *  section D-1): the tenant's "Zonal Head" IS the regional_head role key
 *  (proven equivalence -- docs/security/rbac-and-rls.md; the discount
 *  default authority). The UI speaks the tenant's word; the role KEY on
 *  the wire stays regional_head (no second role token). */
export const DISCOUNT_APPROVER_ROLE_LABELS: Readonly<Record<string, string>> = {
  regional_head: 'Zonal Head',
  owner: 'Owner',
  gm_agm: 'GM / AGM',
  coc: 'COC',
  doctor: 'Doctor',
};

export function discountApproverRoleLabel(token: string): string {
  return DISCOUNT_APPROVER_ROLE_LABELS[token] ?? token;
}

// ---- Doors (EXISTING lawful flows only; LES-022) ----------------------------

/**
 * The href for the primary action, or null when the action renders as an
 * honest state chip (no lawful door exists for the caller/episode).
 *   - record_payment: THE billing-entry prefill contract (this page) --
 *     returns null here because the panel already sits ON the payment door.
 *   - complete_checkout: the /walkin day board (the fulfil act's home).
 *   - schedule_next_appointment: the lead workbench scheduler when a lead
 *     link exists (the ONE-PICKER law: the slot grid lives there); null for
 *     lead-less episodes (an honest "scheduling unavailable" chip).
 *   - review_unresolved_items: the doctor owns decisions -- a chip, no door.
 */
export function checkoutPrimaryActionHref(
  handoff: CheckoutHandoffResponse,
): string | null {
  switch (handoff.primaryAction.action) {
    case 'complete_checkout':
      return '/walkin';
    case 'schedule_next_appointment':
      return handoff.leadId !== null
        ? `/dashboard/leads/${encodeURIComponent(String(handoff.leadId))}`
        : null;
    case 'record_payment':
    case 'review_unresolved_items':
    case 'no_action_required':
    default:
      return null;
  }
}

// ---- Discount form validation (pure; mirrors computeDiscount) ---------------

export interface DiscountFormState {
  readonly kind: 'percent' | 'fixed';
  /** Raw input strings (integer-only; string math -- no float). */
  readonly percentRaw: string;
  readonly amountRaw: string;
  readonly reasonCode: string;
}

export const EMPTY_DISCOUNT_FORM: DiscountFormState = {
  kind: 'percent',
  percentRaw: '',
  amountRaw: '',
  reasonCode: '',
};

/** Parse a whole positive integer from a raw input (string math). */
function parseWholeInt(raw: string): number | null {
  const t = raw.trim();
  if (!/^[0-9]+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export interface DiscountFormValidation {
  readonly ok: boolean;
  readonly error: string | null;
  readonly percent: number | null;
  readonly amountInr: number | null;
}

/** Validate the discount form against the KNOWN accepted estimate. Pure;
 *  the backend re-validates everything (this only keeps the UI honest). */
export function validateDiscountForm(
  form: DiscountFormState,
  acceptedValueInr: number | null,
): DiscountFormValidation {
  const none: DiscountFormValidation = {
    ok: false,
    error: null,
    percent: null,
    amountInr: null,
  };
  if (acceptedValueInr === null || acceptedValueInr <= 0) {
    return { ...none, error: 'No valued accepted estimate to discount.' };
  }
  if (form.reasonCode.trim() === '') {
    return { ...none, error: 'A discount reason is required.' };
  }
  if (form.kind === 'percent') {
    const p = parseWholeInt(form.percentRaw);
    if (p === null || p < 1 || p > 100) {
      return { ...none, error: 'Percent must be a whole number from 1 to 100.' };
    }
    const amount = Math.floor((acceptedValueInr * p) / 100);
    if (amount <= 0) {
      return {
        ...none,
        error: 'This percent rounds to zero rupees on the accepted estimate.',
      };
    }
    return { ok: true, error: null, percent: p, amountInr: null };
  }
  const a = parseWholeInt(form.amountRaw);
  if (a === null) {
    return { ...none, error: 'Amount must be a whole rupee number.' };
  }
  if (a > acceptedValueInr) {
    return { ...none, error: 'The discount cannot exceed the accepted estimate.' };
  }
  return { ok: true, error: null, percent: null, amountInr: a };
}
